<template>
  <section>
    <SHeading level="2" size="2">
      {{
        tr("main_site.company_wall.reviews_title", {
          profileTitle: customerName,
          amount: totalRecords,
        })
      }}
    </SHeading>
    <p
      class="company-wall-details-reviews__description"
      v-html="reviewDescription"
    />
    <div ref="companyWallDetailsReviewsEl" class="company-wall-details-reviews">
      <template v-if="isLoading || state.status === 'success'">
        <SReviewsSkeleton v-if="isLoading" />
        <p v-else-if="reviews.length === 0">
          {{ tr("main_site.company_wall.reviews_no_reviews") }}
        </p>
        <SReviews v-else layout="grid" :reviews="reviews" />

        <SPaginatorSkeleton v-if="isLoading" />
        <SPaginator
          v-else-if="lastPage > 1"
          v-model:current-page="currentPage"
          :items-per-page="perPage"
          :total-items-to-show="lastPage"
          @update:current-page="scrollToTop"
        />
      </template>

      <SErrorMessage
        v-else-if="state.status === 'error'"
        retry
        @retry="refresh"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import { useQuery } from "@pinia/colada";
import { computed, ref, useTemplateRef, watchEffect } from "vue";

import {
  SErrorMessage,
  SHeading,
  SPaginator,
  SPaginatorSkeleton,
  SReviews,
  SReviewsSkeleton,
} from "@solvari/components";
import {
  injectI18nLocale,
  useI18nMainSiteCompanyWall,
} from "@solvari/translations";
import { dayjs } from "@solvari/utils";

import { useCompanyWallReviews } from "~/features/company-wall/api/details.api.ts";
import { useStructuredData } from "~/shared/composables/useMetaData.ts";

const props = defineProps<{
  customerId: number;
  customerName: string;
  customerStructuredDataId: string;
  reviewDescription: string;
}>();

const { tr } = useI18nMainSiteCompanyWall();
const locale = injectI18nLocale().value;

const companyWallDetailsReviewsEl = useTemplateRef<HTMLElement>(
  "companyWallDetailsReviewsEl",
);

const perPage = 10;
const currentPage = ref(1);

const totalRecords = ref(0);
const lastPage = ref(0);

const { state, refresh, isLoading } = useQuery(
  useCompanyWallReviews({
    customerId: props.customerId,
    page: currentPage,
    locale,
  }),
);

const paginatedData = computed(() => state.value.data ?? null);
const reviews = computed(() => paginatedData.value?.data ?? []);

watchEffect(
  () => {
    if (paginatedData.value) {
      totalRecords.value = paginatedData.value.total;
      lastPage.value = paginatedData.value.lastPage;
    }
  },
  { flush: "sync" },
);

function scrollToTop() {
  companyWallDetailsReviewsEl.value?.scrollIntoView({
    behavior: "smooth",
    block: "start",
  });
}

/* eslint-disable @typescript-eslint/naming-convention */
useStructuredData(() => {
  if (!reviews.value.length) {
    return;
  }
  return {
    "@graph": reviews.value.slice(0, 10).map((review) => ({
      "@type": "Review",
      itemReviewed: { "@id": props.customerStructuredDataId },
      datePublished: review.createdAt
        ? dayjs(review.createdAt, locale).toISOString()
        : undefined,
      reviewRating: {
        "@type": "Rating",
        ratingValue: review.ratingAverage,
        bestRating: 5,
        worstRating: 1,
      },
      author: {
        "@type": "Person",
        name: review.author,
      },
    })),
  };
});
/* eslint-enable @typescript-eslint/naming-convention */
</script>

<style lang="postcss">
.company-wall-details-reviews {
  @apply mb-8 scroll-m-16;

  &__description {
    @apply mb-8 text-gray-700;
  }
}
</style>
